import type {
  ChangeEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  TouchEvent as ReactTouchEvent,
} from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import UploadFluidBackground from "./components/UploadFluidBackground";
import "./App.css";

type Popup = {
  id: number;
  kind: "text" | "skip";
  text?: string;
  direction?: "forward" | "backward";
};

type ContextMenuState = {
  visible: boolean;
  x: number;
  y: number;
};

type SeekTooltipState = {
  visible: boolean;
  left: number;
  text: string;
};

type MediaKind = "audio" | "video";

type PlaylistItem = {
  id: number;
  displayName: string;
  file?: File;
  kind: MediaKind;
  url: string;
  revokeOnDispose: boolean;
};

type PlayerSettings = {
  volume: number;
  muted: boolean;
  loop: boolean;
  repeatAll: boolean;
  shuffle: boolean;
  speed: number;
  boost: number;
  showNerdStats: boolean;
};

const BOOST_LEVELS = [1, 2, 4] as const;
const SPEEDS = [0.5, 1, 1.25, 1.5, 2] as const;
const DEFAULT_ACCENT = "rgb(59, 130, 246)";
const DEFAULT_ACCENT_GLOW = "rgba(59, 130, 246, 0.4)";
const DEFAULT_BACKGROUND = "rgb(2, 2, 2)";
const SETTINGS_STORAGE_KEY = "videoplayer.settings.v1";
const SWIPE_MIN_DISTANCE = 60;
const SWIPE_VERTICAL_BIAS = 20;

const AUDIO_EXTENSIONS = [".mp3", ".wav", ".flac", ".aac", ".m4a", ".ogg", ".opus", ".webm"];
const VIDEO_EXTENSIONS = [".mp4", ".mkv", ".mov", ".avi", ".webm", ".wmv", ".m4v", ".ogv"];

const DEFAULT_SETTINGS: PlayerSettings = {
  volume: 1,
  muted: false,
  loop: false,
  repeatAll: false,
  shuffle: false,
  speed: 1,
  boost: 1,
  showNerdStats: false,
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function formatTime(seconds: number): string {
  if (Number.isNaN(seconds) || !Number.isFinite(seconds)) {
    return "00:00";
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
}

function getMediaKind(file: File): MediaKind | null {
  const mime = file.type.toLowerCase();
  if (mime.startsWith("audio/")) {
    return "audio";
  }
  if (mime.startsWith("video/")) {
    return "video";
  }

  const loweredName = file.name.toLowerCase();
  if (AUDIO_EXTENSIONS.some((extension) => loweredName.endsWith(extension))) {
    return "audio";
  }
  if (VIDEO_EXTENSIONS.some((extension) => loweredName.endsWith(extension))) {
    return "video";
  }

  return null;
}

function getMediaKindFromUrl(mediaUrl: string): MediaKind {
  const normalized = mediaUrl.toLowerCase().split("?")[0].split("#")[0];

  if (AUDIO_EXTENSIONS.some((extension) => normalized.endsWith(extension))) {
    return "audio";
  }

  return "video";
}

function getDisplayNameFromUrl(mediaUrl: string): string {
  try {
    const parsed = new URL(mediaUrl);
    const name = decodeURIComponent(parsed.pathname.split("/").pop() ?? "").trim();
    return name || "Remote Media";
  } catch {
    const fallback = mediaUrl.split("/").pop()?.split("?")[0]?.trim();
    return fallback || "Remote Media";
  }
}

function getMediaUrlFromLocation(locationValue: Location): string | null {
  if (locationValue.pathname !== "/vidurl") {
    return null;
  }

  const params = new URLSearchParams(locationValue.search);
  const fromNamedParam = params.get("url") ?? params.get("src") ?? params.get("media");
  if (fromNamedParam?.trim()) {
    return fromNamedParam.trim();
  }

  if (locationValue.search.startsWith("?=")) {
    const raw = locationValue.search.slice(2).trim();
    if (!raw) {
      return null;
    }
    return raw;
  }

  return null;
}

function normalizeMediaUrl(mediaUrl: string): string {
  const trimmed = mediaUrl.trim();

  if (!trimmed) {
    return trimmed;
  }

  try {
    return new URL(trimmed, window.location.href).toString();
  } catch {
    return trimmed.replace(/ /g, "%20");
  }
}

function loadPlayerSettings(): PlayerSettings {
  if (typeof window === "undefined") {
    return { ...DEFAULT_SETTINGS };
  }

  try {
    const raw = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) {
      return { ...DEFAULT_SETTINGS };
    }

    const parsed = JSON.parse(raw) as Partial<PlayerSettings>;

    const speed = SPEEDS.includes(parsed.speed as (typeof SPEEDS)[number])
      ? (parsed.speed as (typeof SPEEDS)[number])
      : DEFAULT_SETTINGS.speed;
    const boost = BOOST_LEVELS.includes(parsed.boost as (typeof BOOST_LEVELS)[number])
      ? (parsed.boost as (typeof BOOST_LEVELS)[number])
      : DEFAULT_SETTINGS.boost;

    return {
      volume:
        typeof parsed.volume === "number" && Number.isFinite(parsed.volume)
          ? clamp(parsed.volume, 0, 1)
          : DEFAULT_SETTINGS.volume,
      muted: typeof parsed.muted === "boolean" ? parsed.muted : DEFAULT_SETTINGS.muted,
      loop: typeof parsed.loop === "boolean" ? parsed.loop : DEFAULT_SETTINGS.loop,
      repeatAll:
        typeof parsed.repeatAll === "boolean"
          ? parsed.repeatAll
          : DEFAULT_SETTINGS.repeatAll,
      shuffle: typeof parsed.shuffle === "boolean" ? parsed.shuffle : DEFAULT_SETTINGS.shuffle,
      speed,
      boost,
      showNerdStats:
        typeof parsed.showNerdStats === "boolean"
          ? parsed.showNerdStats
          : DEFAULT_SETTINGS.showNerdStats,
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export default function App() {
  const playerRootRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const visualizerRef = useRef<HTMLCanvasElement | null>(null);
  const seekAreaRef = useRef<HTMLDivElement | null>(null);
  const ambientGlowRef = useRef<HTMLDivElement | null>(null);
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);

  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);

  const analysisCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const analysisCtxRef = useRef<CanvasRenderingContext2D | null>(null);

  const visualizerRafRef = useRef<number | null>(null);
  const ambientRafRef = useRef<number | null>(null);
  const idleTimerRef = useRef<number | null>(null);
  const popupIdRef = useRef(1);
  const startupUrlLoadedRef = useRef(false);
  const playlistIdRef = useRef(1);
  const playlistRef = useRef<PlaylistItem[]>([]);
  const currentTrackIndexRef = useRef(-1);

  const initialSettings = useMemo(() => loadPlayerSettings(), []);
  const initialSpeedIndex = useMemo(() => {
    const index = SPEEDS.findIndex((speed) => speed === initialSettings.speed);
    return index >= 0 ? index : 1;
  }, [initialSettings.speed]);

  const [hasMedia, setHasMedia] = useState(false);
  const [isAudioFile, setIsAudioFile] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoop, setIsLoop] = useState(initialSettings.loop);
  const [repeatAll, setRepeatAll] = useState(initialSettings.repeatAll);
  const [shuffleMode, setShuffleMode] = useState(initialSettings.shuffle);
  const [forceHide, setForceHide] = useState(false);
  const [hideUi, setHideUi] = useState(false);
  const [hoveringControls, setHoveringControls] = useState(false);
  const [showNerdStats, setShowNerdStats] = useState(initialSettings.showNerdStats);
  const [muted, setMuted] = useState(initialSettings.muted);
  const [touchUi, setTouchUi] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.matchMedia("(hover: none), (pointer: coarse)").matches;
  });

  const [audioTitle, setAudioTitle] = useState("");
  const [albumArtUrl, setAlbumArtUrl] = useState<string | null>(null);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bufferedAhead, setBufferedAhead] = useState(0);
  const [resolution, setResolution] = useState("N/A");

  const [currentBoost, setCurrentBoost] = useState<number>(initialSettings.boost);
  const [speedIndex, setSpeedIndex] = useState(initialSpeedIndex);
  const [playbackRate, setPlaybackRate] = useState(initialSettings.speed);
  const [volume, setVolume] = useState(initialSettings.volume);

  const [pipSupported, setPipSupported] = useState(false);
  const [playlist, setPlaylist] = useState<PlaylistItem[]>([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(-1);
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    visible: false,
    x: 0,
    y: 0,
  });
  const [seekTooltip, setSeekTooltip] = useState<SeekTooltipState>({
    visible: false,
    left: 0,
    text: "00:00",
  });
  const [popups, setPopups] = useState<Popup[]>([]);

  const applyTheme = useCallback((r: number, g: number, b: number) => {
    const red = Math.round(clamp(r, 0, 255));
    const green = Math.round(clamp(g, 0, 255));
    const blue = Math.round(clamp(b, 0, 255));

    document.documentElement.style.setProperty("--accent", `rgb(${red}, ${green}, ${blue})`);
    document.documentElement.style.setProperty("--accent-glow", `rgba(${red}, ${green}, ${blue}, 0.45)`);
    document.body.style.backgroundColor = `rgb(${Math.max(0, Math.round(red / 16))}, ${Math.max(0, Math.round(green / 16))}, ${Math.max(0, Math.round(blue / 16))})`;

    if (ambientGlowRef.current) {
      ambientGlowRef.current.style.background = `radial-gradient(circle, rgb(${red}, ${green}, ${blue}) 0%, transparent 80%)`;
    }
  }, []);

  const resetTheme = useCallback(() => {
    document.documentElement.style.setProperty("--accent", DEFAULT_ACCENT);
    document.documentElement.style.setProperty("--accent-glow", DEFAULT_ACCENT_GLOW);
    document.body.style.backgroundColor = DEFAULT_BACKGROUND;
    if (ambientGlowRef.current) {
      ambientGlowRef.current.style.background = "none";
    }
  }, []);

  const removePopup = useCallback((id: number) => {
    setPopups((prev) => prev.filter((popup) => popup.id !== id));
  }, []);

  const showPopup = useCallback(
    (text: string) => {
      const id = popupIdRef.current;
      popupIdRef.current += 1;

      setPopups((prev) => [...prev, { id, kind: "text", text }]);
      window.setTimeout(() => removePopup(id), 600);
    },
    [removePopup],
  );

  const showSkipPopup = useCallback(
    (direction: "forward" | "backward") => {
      const id = popupIdRef.current;
      popupIdRef.current += 1;

      setPopups((prev) => [...prev, { id, kind: "skip", direction }]);
      window.setTimeout(() => removePopup(id), 600);
    },
    [removePopup],
  );

  const initAudio = useCallback(() => {
    const video = videoRef.current;
    if (!video || audioCtxRef.current) {
      return;
    }

    const AudioCtor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

    if (!AudioCtor) {
      return;
    }

    const audioCtx = new AudioCtor();
    const source = audioCtx.createMediaElementSource(video);
    const analyser = audioCtx.createAnalyser();
    const gainNode = audioCtx.createGain();

    source.connect(analyser);
    analyser.connect(gainNode);
    gainNode.connect(audioCtx.destination);

    analyser.fftSize = 512;
    gainNode.gain.value = currentBoost;

    sourceRef.current = source;
    analyserRef.current = analyser;
    gainNodeRef.current = gainNode;
    audioCtxRef.current = audioCtx;
    dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);
  }, [currentBoost]);

  const resetIdleTimer = useCallback(() => {
    if (forceHide) {
      return;
    }

    if (touchUi) {
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      setHideUi(false);
      return;
    }

    setHideUi(false);

    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }

    if (hasMedia && !hoveringControls) {
      idleTimerRef.current = window.setTimeout(() => {
        setHideUi(true);
      }, 2200);
    }
  }, [forceHide, hasMedia, hoveringControls, touchUi]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video || !hasMedia) {
      return;
    }

    if (audioCtxRef.current?.state === "suspended") {
      void audioCtxRef.current.resume();
    }

    if (video.paused) {
      void video.play().catch(() => {
        showPopup("PRESS PLAY");
      });
    } else {
      video.pause();
    }

    resetIdleTimer();
  }, [hasMedia, resetIdleTimer, showPopup]);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    video.muted = !video.muted;
    setMuted(video.muted);
    if (!video.muted && video.volume === 0) {
      video.volume = 0.4;
      setVolume(0.4);
    }
    showPopup(video.muted ? "MUTED" : "UNMUTED");
  }, [showPopup]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      void document.documentElement.requestFullscreen();
      return;
    }
    void document.exitFullscreen();
  }, []);

  const toggleLoop = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const nextLoopState = !video.loop;
    video.loop = nextLoopState;
    setIsLoop(nextLoopState);

    if (nextLoopState && repeatAll) {
      setRepeatAll(false);
    }

    showPopup(`LOOP ONE: ${nextLoopState ? "ON" : "OFF"}`);
  }, [repeatAll, showPopup]);

  const toggleRepeatAll = useCallback(() => {
    setRepeatAll((prev) => {
      const next = !prev;
      if (next && isLoop) {
        setIsLoop(false);
        const video = videoRef.current;
        if (video) {
          video.loop = false;
        }
      }
      showPopup(`REPEAT ALL: ${next ? "ON" : "OFF"}`);
      return next;
    });
  }, [isLoop, showPopup]);

  const toggleShuffleMode = useCallback(() => {
    setShuffleMode((prev) => {
      const next = !prev;
      showPopup(`SHUFFLE: ${next ? "ON" : "OFF"}`);
      return next;
    });
  }, [showPopup]);

  const cycleBoost = useCallback(() => {
    const activeTrack = playlistRef.current[currentTrackIndexRef.current];
    if (activeTrack && !activeTrack.revokeOnDispose) {
      showPopup("BOOST DISABLED FOR URL");
      return;
    }

    initAudio();

    if (audioCtxRef.current?.state === "suspended") {
      void audioCtxRef.current.resume();
    }

    const boostIndex = BOOST_LEVELS.findIndex((level) => level === currentBoost);
    const nextBoost = BOOST_LEVELS[(boostIndex + 1 + BOOST_LEVELS.length) % BOOST_LEVELS.length];

    setCurrentBoost(nextBoost);

    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = nextBoost;
    }

    showPopup(`BOOST: ${nextBoost * 100}%`);
  }, [currentBoost, initAudio, showPopup]);

  const cycleSpeed = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const nextIndex = (speedIndex + 1) % SPEEDS.length;
    const speed = SPEEDS[nextIndex];

    setSpeedIndex(nextIndex);
    setPlaybackRate(speed);
    video.playbackRate = speed;

    showPopup(`SPEED: ${speed}x`);
  }, [showPopup, speedIndex]);

  const togglePIP = useCallback(async () => {
    const video = videoRef.current;

    if (
      !video ||
      isAudioFile ||
      !pipSupported ||
      typeof video.requestPictureInPicture !== "function"
    ) {
      return;
    }

    try {
      if (video !== document.pictureInPictureElement) {
        await video.requestPictureInPicture();
      } else if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      }
    } catch {
      showPopup("PIP Failed");
    }
  }, [isAudioFile, pipSupported, showPopup]);

  const takeSnapshot = useCallback(() => {
    const video = videoRef.current;

    if (!video || isAudioFile || video.videoWidth === 0 || video.videoHeight === 0) {
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    context.drawImage(video, 0, 0);

    const link = document.createElement("a");
    link.download = `snapshot_${Date.now()}.png`;
    link.href = canvas.toDataURL();
    link.click();

    showPopup("SNAPSHOT SAVED");
  }, [isAudioFile, showPopup]);

  const showMusicPlaceholder = useCallback(() => {
    setAlbumArtUrl(null);
    resetTheme();
  }, [resetTheme]);

  const analyzeImageColor = useCallback(
    (url: string) => {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = 1;
        canvas.height = 1;

        const context = canvas.getContext("2d");
        if (!context) {
          resetTheme();
          return;
        }

        context.drawImage(img, 0, 0, 1, 1);
        const pixel = context.getImageData(0, 0, 1, 1).data;
        applyTheme(pixel[0], pixel[1], pixel[2]);
      };
      img.onerror = () => {
        resetTheme();
      };
    },
    [applyTheme, resetTheme],
  );

  const extractMetadata = useCallback(
    (file: File) => {
      const metadataReader = window.jsmediatags;

      if (!metadataReader) {
        showMusicPlaceholder();
        return;
      }

      metadataReader.read(file, {
        onSuccess: (result) => {
          const picture = result.tags.picture;

          if (!picture?.data?.length || !picture.format) {
            showMusicPlaceholder();
            return;
          }

          const bytes = new Uint8Array(picture.data);
          let binary = "";
          for (let i = 0; i < bytes.length; i += 1) {
            binary += String.fromCharCode(bytes[i]);
          }

          const imageUrl = `data:${picture.format};base64,${window.btoa(binary)}`;
          setAlbumArtUrl(imageUrl);
          analyzeImageColor(imageUrl);
        },
        onError: () => {
          showMusicPlaceholder();
        },
      });
    },
    [analyzeImageColor, showMusicPlaceholder],
  );

  const revokePlaylistUrls = useCallback((items: PlaylistItem[]) => {
    items.forEach((item) => {
      if (item.revokeOnDispose) {
        URL.revokeObjectURL(item.url);
      }
    });
  }, []);

  const loadPlaylistTrack = useCallback(
    (track: PlaylistItem, trackIndex: number, autoPlay = true) => {
      const video = videoRef.current;
      if (!video) {
        return;
      }

      const shouldEnableAudioGraph = track.revokeOnDispose;

      if (shouldEnableAudioGraph) {
        initAudio();
        if (audioCtxRef.current?.state === "suspended") {
          void audioCtxRef.current.resume();
        }
      }

      const nextIsAudio = track.kind === "audio";

      currentTrackIndexRef.current = trackIndex;
      setCurrentTrackIndex(trackIndex);
      setHasMedia(true);
      setIsAudioFile(nextIsAudio);
      setAudioTitle(track.displayName);
      setCurrentTime(0);
      setDuration(0);
      setBufferedAhead(0);
      setResolution(nextIsAudio ? "N/A (Audio)" : "0x0");
      setContextMenu((prev) => ({ ...prev, visible: false }));

      if (nextIsAudio) {
        setAlbumArtUrl(null);
        if (track.file) {
          extractMetadata(track.file);
        } else {
          showMusicPlaceholder();
        }
      } else {
        setAlbumArtUrl(null);
        resetTheme();
      }

      try {
        const parsed = new URL(track.url, window.location.href);
        const sameOrigin = parsed.origin === window.location.origin;
        video.crossOrigin = shouldEnableAudioGraph || sameOrigin ? "anonymous" : null;
      } catch {
        video.crossOrigin = shouldEnableAudioGraph ? "anonymous" : null;
      }

      video.src = track.url;
      video.loop = isLoop;
      video.playbackRate = playbackRate;
      video.volume = volume;
      video.muted = muted;
      if (shouldEnableAudioGraph && gainNodeRef.current) {
        gainNodeRef.current.gain.value = currentBoost;
      }
      video.load();

      if (autoPlay) {
        void video.play().catch(() => {
          showPopup("PRESS PLAY");
        });
      }

      resetIdleTimer();
    },
    [
      currentBoost,
      extractMetadata,
      initAudio,
      isLoop,
      muted,
      playbackRate,
      resetIdleTimer,
      resetTheme,
      showPopup,
      volume,
    ],
  );

  const replacePlaylist = useCallback(
    (files: File[]) => {
      if (!files.length) {
        return;
      }

      const tracks: PlaylistItem[] = [];
      let skippedCount = 0;

      files.forEach((file) => {
        const mediaKind = getMediaKind(file);
        if (!mediaKind) {
          skippedCount += 1;
          return;
        }
        tracks.push({
          id: playlistIdRef.current,
          displayName: file.name,
          file,
          kind: mediaKind,
          url: URL.createObjectURL(file),
          revokeOnDispose: true,
        });
        playlistIdRef.current += 1;
      });

      if (!tracks.length) {
        showPopup("UNSUPPORTED FILE");
        return;
      }

      if (skippedCount > 0) {
        showPopup(`${skippedCount} FILES SKIPPED`);
      }

      revokePlaylistUrls(playlistRef.current);
      playlistRef.current = tracks;
      setPlaylist(tracks);

      loadPlaylistTrack(tracks[0], 0, true);
    },
    [loadPlaylistTrack, revokePlaylistUrls, showPopup],
  );

  const getAdjacentTrackIndex = useCallback(
    (direction: 1 | -1): number => {
      const tracks = playlistRef.current;
      const currentIndex = currentTrackIndexRef.current;

      if (!tracks.length) {
        return -1;
      }

      if (shuffleMode && tracks.length > 1) {
        let randomIndex = currentIndex;
        while (randomIndex === currentIndex) {
          randomIndex = Math.floor(Math.random() * tracks.length);
        }
        return randomIndex;
      }

      const candidateIndex = currentIndex + direction;
      if (candidateIndex >= 0 && candidateIndex < tracks.length) {
        return candidateIndex;
      }

      if (repeatAll) {
        return direction > 0 ? 0 : tracks.length - 1;
      }

      return -1;
    },
    [repeatAll, shuffleMode],
  );

  const playTrackAtIndex = useCallback(
    (index: number, autoPlay = true) => {
      const tracks = playlistRef.current;
      if (index < 0 || index >= tracks.length) {
        return false;
      }
      loadPlaylistTrack(tracks[index], index, autoPlay);
      return true;
    },
    [loadPlaylistTrack],
  );

  const playNextTrack = useCallback(
    (reason: "auto" | "manual" = "manual") => {
      const nextIndex = getAdjacentTrackIndex(1);
      if (nextIndex < 0) {
        if (reason === "manual") {
          showPopup("END OF QUEUE");
        }
        return false;
      }
      return playTrackAtIndex(nextIndex, true);
    },
    [getAdjacentTrackIndex, playTrackAtIndex, showPopup],
  );

  const playPreviousTrack = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return false;
    }

    if (video.currentTime > 3) {
      video.currentTime = 0;
      showPopup("RESTART");
      return true;
    }

    const previousIndex = getAdjacentTrackIndex(-1);
    if (previousIndex < 0) {
      showPopup("START OF QUEUE");
      return false;
    }
    return playTrackAtIndex(previousIndex, true);
  }, [getAdjacentTrackIndex, playTrackAtIndex, showPopup]);

  const onFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files ? Array.from(event.target.files) : [];
      replacePlaylist(files);
      event.target.value = "";
    },
    [replacePlaylist],
  );

  const onSeekInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const video = videoRef.current;
      if (!video || !duration) {
        return;
      }

      const value = Number(event.target.value);
      video.currentTime = (value / 100) * duration;
      setCurrentTime(video.currentTime);
      resetIdleTimer();
    },
    [duration, resetIdleTimer],
  );

  const onSeekMouseMove = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      const video = videoRef.current;
      const seekArea = seekAreaRef.current;

      if (!video || !seekArea || !duration) {
        return;
      }

      const rect = seekArea.getBoundingClientRect();
      const x = clamp(event.clientX - rect.left, 0, rect.width);
      const percent = x / rect.width;
      const timeAtMouse = percent * duration;

      setSeekTooltip({
        visible: true,
        left: x,
        text: formatTime(timeAtMouse),
      });

      resetIdleTimer();
    },
    [duration, resetIdleTimer],
  );

  const onVolumeInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const video = videoRef.current;
      if (!video) {
        return;
      }

      const nextVolume = Number(event.target.value);
      if (video.muted && nextVolume > 0) {
        video.muted = false;
      }
      video.volume = nextVolume;
      setMuted(video.muted);
      setVolume(nextVolume);
      resetIdleTimer();
    },
    [resetIdleTimer],
  );

  const onDropZoneKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      fileInputRef.current?.click();
    }
  };

  const onControlClick = (event: ReactMouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const onControlsMouseEnter = () => {
    setHoveringControls(true);
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    setHideUi(false);
  };

  const onControlsMouseLeave = () => {
    setHoveringControls(false);
    resetIdleTimer();
  };

  const runContextAction = useCallback((action: () => void) => {
    action();
    setContextMenu((prev) => ({ ...prev, visible: false }));
  }, []);

  useEffect(() => {
    const canUsePip =
      "pictureInPictureEnabled" in document &&
      Boolean(
        (document as Document & { pictureInPictureEnabled?: boolean })
          .pictureInPictureEnabled,
      );
    setPipSupported(canUsePip);

    const mediaQuery = window.matchMedia("(hover: none), (pointer: coarse)");
    setTouchUi(mediaQuery.matches);
    const onTouchModeChange = (event: MediaQueryListEvent) => {
      setTouchUi(event.matches);
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", onTouchModeChange);
    } else {
      mediaQuery.addListener(onTouchModeChange);
    }

    resetTheme();

    return () => {
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", onTouchModeChange);
      } else {
        mediaQuery.removeListener(onTouchModeChange);
      }
      document.documentElement.style.setProperty("--accent", DEFAULT_ACCENT);
      document.documentElement.style.setProperty("--accent-glow", DEFAULT_ACCENT_GLOW);
      document.body.style.backgroundColor = DEFAULT_BACKGROUND;
    };
  }, [resetTheme]);

  useEffect(() => {
    const nextSettings: PlayerSettings = {
      volume,
      muted,
      loop: isLoop,
      repeatAll,
      shuffle: shuffleMode,
      speed: playbackRate,
      boost: currentBoost,
      showNerdStats,
    };

    try {
      window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(nextSettings));
    } catch {
      // Ignore storage failures in private mode or restricted contexts.
    }
  }, [
    currentBoost,
    isLoop,
    muted,
    playbackRate,
    repeatAll,
    showNerdStats,
    shuffleMode,
    volume,
  ]);

  useEffect(() => {
    playlistRef.current = playlist;
  }, [playlist]);

  useEffect(() => {
    currentTrackIndexRef.current = currentTrackIndex;
  }, [currentTrackIndex]);

  useEffect(() => {
    if (startupUrlLoadedRef.current) {
      return;
    }
    startupUrlLoadedRef.current = true;

    const mediaUrl = getMediaUrlFromLocation(window.location);
    if (!mediaUrl) {
      return;
    }

    const normalizedUrl = normalizeMediaUrl(mediaUrl);
    if (!normalizedUrl) {
      return;
    }

    const track: PlaylistItem = {
      id: playlistIdRef.current,
      displayName: getDisplayNameFromUrl(normalizedUrl),
      kind: getMediaKindFromUrl(normalizedUrl),
      url: normalizedUrl,
      revokeOnDispose: false,
    };
    playlistIdRef.current += 1;

    revokePlaylistUrls(playlistRef.current);
    playlistRef.current = [track];
    setPlaylist([track]);
    loadPlaylistTrack(track, 0, true);
    showPopup("LOADED URL");
  }, [loadPlaylistTrack, revokePlaylistUrls, showPopup]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    video.loop = isLoop;
  }, [isLoop]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    video.playbackRate = playbackRate;
  }, [playbackRate]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    video.volume = volume;
    video.muted = muted;
  }, [muted, volume]);

  useEffect(() => {
    if (!gainNodeRef.current) {
      return;
    }
    gainNodeRef.current.gain.value = currentBoost;
  }, [currentBoost]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const onPlay = () => {
      setIsPlaying(true);
      resetIdleTimer();
    };

    const onPause = () => {
      setIsPlaying(false);
      if (!forceHide) {
        setHideUi(false);
      }
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };

    const onTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      if (Number.isFinite(video.duration)) {
        setDuration(video.duration);
      }
    };

    const onLoadedMetadata = () => {
      setDuration(Number.isFinite(video.duration) ? video.duration : 0);
      if (isAudioFile) {
        setResolution("N/A (Audio)");
      } else {
        setResolution(`${video.videoWidth}x${video.videoHeight}`);
      }
    };

    const onVolumeChange = () => {
      setVolume(video.volume);
      setMuted(video.muted);
    };

    const onRateChange = () => {
      setPlaybackRate(video.playbackRate);
      const existingIndex = SPEEDS.findIndex((value) => value === video.playbackRate);
      if (existingIndex >= 0) {
        setSpeedIndex(existingIndex);
      }
    };

    const onEnded = () => {
      if (!isLoop) {
        playNextTrack("auto");
      }
    };

    const onError = () => {
      if (playNextTrack("auto")) {
        showPopup("SKIPPED BROKEN TRACK");
        return;
      }

      const currentPlaylist = playlistRef.current;

      setHasMedia(false);
      setIsPlaying(false);
      setIsAudioFile(false);
      setAlbumArtUrl(null);
      setAudioTitle("");
      setCurrentTime(0);
      setDuration(0);
      setBufferedAhead(0);
      setResolution("N/A");
      setSeekTooltip((prev) => ({ ...prev, visible: false }));
      setPlaylist([]);
      setCurrentTrackIndex(-1);
      revokePlaylistUrls(currentPlaylist);
      playlistRef.current = [];
      currentTrackIndexRef.current = -1;

      video.removeAttribute("src");
      video.load();
      resetTheme();
      showPopup("FAILED TO LOAD MEDIA");
    };

    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("volumechange", onVolumeChange);
    video.addEventListener("ratechange", onRateChange);
    video.addEventListener("ended", onEnded);
    video.addEventListener("error", onError);

    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("volumechange", onVolumeChange);
      video.removeEventListener("ratechange", onRateChange);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("error", onError);
    };
  }, [
    forceHide,
    isAudioFile,
    isLoop,
    playNextTrack,
    resetIdleTimer,
    resetTheme,
    revokePlaylistUrls,
    showPopup,
  ]);

  useEffect(() => {
    if (!showNerdStats) {
      return;
    }

    const interval = window.setInterval(() => {
      const video = videoRef.current;
      if (!video) {
        return;
      }

      const buffered =
        video.buffered.length > 0
          ? video.buffered.end(video.buffered.length - 1) - video.currentTime
          : 0;

      setBufferedAhead(Number(buffered.toFixed(2)));
    }, 100);

    return () => {
      window.clearInterval(interval);
    };
  }, [showNerdStats]);

  useEffect(() => {
    if (!isAudioFile) {
      if (visualizerRafRef.current) {
        window.cancelAnimationFrame(visualizerRafRef.current);
        visualizerRafRef.current = null;
      }
      return;
    }

    const canvas = visualizerRef.current;
    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;

    if (!canvas || !analyser || !dataArray) {
      return;
    }

    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    const drawVisualizer = () => {
      visualizerRafRef.current = window.requestAnimationFrame(drawVisualizer);

      if (!isAudioFile) {
        return;
      }

      (analyser.getByteFrequencyData as (buffer: Uint8Array) => void)(dataArray);
      canvas.width = window.innerWidth;
      canvas.height = 280;

      context.clearRect(0, 0, canvas.width, canvas.height);

      const barWidth = (canvas.width / dataArray.length) * 1.5;
      let x = 0;

      for (let i = 0; i < dataArray.length; i += 1) {
        const value = dataArray[i];
        const barHeight = (value / 255) * canvas.height;

        const gradient = context.createLinearGradient(0, canvas.height, 0, 0);
        gradient.addColorStop(0, getComputedStyle(document.documentElement).getPropertyValue("--accent"));
        gradient.addColorStop(1, "rgba(255,255,255,0.4)");

        context.fillStyle = gradient;
        context.fillRect(x, canvas.height - barHeight, barWidth - 1, barHeight);
        x += barWidth;
      }
    };

    drawVisualizer();

    return () => {
      if (visualizerRafRef.current) {
        window.cancelAnimationFrame(visualizerRafRef.current);
        visualizerRafRef.current = null;
      }
    };
  }, [isAudioFile]);

  useEffect(() => {
    if (isAudioFile || !isPlaying) {
      if (ambientRafRef.current) {
        window.cancelAnimationFrame(ambientRafRef.current);
        ambientRafRef.current = null;
      }
      return;
    }

    if (!analysisCanvasRef.current) {
      analysisCanvasRef.current = document.createElement("canvas");
      analysisCanvasRef.current.width = 10;
      analysisCanvasRef.current.height = 10;
      analysisCtxRef.current = analysisCanvasRef.current.getContext("2d", {
        willReadFrequently: true,
      });
    }

    const sampleFrame = () => {
      const video = videoRef.current;
      const canvas = analysisCanvasRef.current;
      const context = analysisCtxRef.current;

      if (!video || !canvas || !context || video.paused || video.ended || isAudioFile) {
        return;
      }

      try {
        canvas.width = 10;
        canvas.height = 10;
        context.drawImage(video, 0, 0, 10, 10);
        const pixelData = context.getImageData(0, 0, 10, 10).data;

        let red = 0;
        let green = 0;
        let blue = 0;

        for (let i = 0; i < pixelData.length; i += 4) {
          red += pixelData[i];
          green += pixelData[i + 1];
          blue += pixelData[i + 2];
        }

        const totalPixels = pixelData.length / 4;
        applyTheme(red / totalPixels, green / totalPixels, blue / totalPixels);
      } catch {
        // Ignore frames that fail to sample.
      }

      ambientRafRef.current = window.requestAnimationFrame(sampleFrame);
    };

    ambientRafRef.current = window.requestAnimationFrame(sampleFrame);

    return () => {
      if (ambientRafRef.current) {
        window.cancelAnimationFrame(ambientRafRef.current);
        ambientRafRef.current = null;
      }
    };
  }, [applyTheme, isAudioFile, isPlaying]);

  useEffect(() => {
    const isInsidePlayer = (target: EventTarget | null) => {
      const root = playerRootRef.current;
      return root && target instanceof Node ? root.contains(target) : false;
    };

    const onClick = (event: MouseEvent) => {
      if (isInsidePlayer(event.target)) {
        resetIdleTimer();
      }
      setContextMenu((prev) => (prev.visible ? { ...prev, visible: false } : prev));
    };

    const onMove = (event: MouseEvent) => {
      if (isInsidePlayer(event.target)) {
        resetIdleTimer();
      }
    };

    const onContextMenu = (event: MouseEvent) => {
      if (!hasMedia || !isInsidePlayer(event.target)) {
        return;
      }
      event.preventDefault();
      setContextMenu({
        visible: true,
        x: Math.min(event.pageX, window.innerWidth - 240),
        y: Math.min(event.pageY, window.innerHeight - 250),
      });
    };

    window.addEventListener("click", onClick);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("contextmenu", onContextMenu);

    return () => {
      window.removeEventListener("click", onClick);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("contextmenu", onContextMenu);
    };
  }, [hasMedia, resetIdleTimer]);

  useEffect(() => {
    const onDragOver = (event: DragEvent) => {
      event.preventDefault();
    };

    const onDrop = (event: DragEvent) => {
      event.preventDefault();
      const droppedFiles = event.dataTransfer?.files
        ? Array.from(event.dataTransfer.files)
        : [];
      replacePlaylist(droppedFiles);
    };

    document.body.addEventListener("dragover", onDragOver);
    document.body.addEventListener("drop", onDrop);

    return () => {
      document.body.removeEventListener("dragover", onDragOver);
      document.body.removeEventListener("drop", onDrop);
    };
  }, [replacePlaylist]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      resetIdleTimer();
      const video = videoRef.current;

      const targetElement = event.target as HTMLElement | null;
      const isFormField =
        targetElement?.tagName === "INPUT" ||
        targetElement?.tagName === "TEXTAREA" ||
        targetElement?.tagName === "SELECT" ||
        Boolean(targetElement?.isContentEditable);

      if (isFormField) {
        return;
      }

      if (event.code === "Escape") {
        setContextMenu((prev) => (prev.visible ? { ...prev, visible: false } : prev));
      }

      if (event.key === "Shift") {
        setForceHide((prev) => {
          const next = !prev;
          setHideUi(next);
          showPopup(next ? "UI HIDDEN" : "UI VISIBLE");
          if (!next) {
            window.setTimeout(() => {
              resetIdleTimer();
            }, 0);
          }
          return next;
        });
        return;
      }

      if (!video || !hasMedia) {
        return;
      }

      if (event.code === "Space") {
        event.preventDefault();
        togglePlay();
      } else if (event.code === "ArrowLeft") {
        video.currentTime = Math.max(0, video.currentTime - 5);
        showSkipPopup("backward");
      } else if (event.code === "ArrowRight") {
        video.currentTime = Math.min(duration || Number.POSITIVE_INFINITY, video.currentTime + 5);
        showSkipPopup("forward");
      } else if (event.code === "ArrowUp") {
        video.volume = clamp(video.volume + 0.1, 0, 1);
        if (video.volume > 0 && video.muted) {
          video.muted = false;
        }
        showPopup(`VOL: ${Math.round(video.volume * 100)}%`);
      } else if (event.code === "ArrowDown") {
        video.volume = clamp(video.volume - 0.1, 0, 1);
        if (video.volume > 0 && video.muted) {
          video.muted = false;
        }
        showPopup(`VOL: ${Math.round(video.volume * 100)}%`);
      } else if (event.code === "KeyM") {
        toggleMute();
      } else if (event.code === "KeyF") {
        toggleFullscreen();
      } else if (event.code === "KeyB") {
        cycleBoost();
      } else if (event.code === "KeyS") {
        takeSnapshot();
      } else if (event.code === "KeyL") {
        toggleLoop();
      } else if (event.code === "KeyP") {
        void togglePIP();
      } else if (event.code === "KeyN") {
        setShowNerdStats((prev) => !prev);
      } else if (event.code === "KeyJ") {
        playPreviousTrack();
      } else if (event.code === "KeyK") {
        playNextTrack("manual");
      } else if (event.code === "KeyH") {
        toggleShuffleMode();
      } else if (event.code === "KeyR") {
        toggleRepeatAll();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [
    cycleBoost,
    duration,
    hasMedia,
    resetIdleTimer,
    showPopup,
    showSkipPopup,
    takeSnapshot,
    toggleFullscreen,
    toggleLoop,
    toggleMute,
    toggleRepeatAll,
    toggleShuffleMode,
    togglePIP,
    togglePlay,
    playNextTrack,
    playPreviousTrack,
  ]);

  useEffect(() => {
    return () => {
      revokePlaylistUrls(playlistRef.current);
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
      }
      if (visualizerRafRef.current) {
        window.cancelAnimationFrame(visualizerRafRef.current);
      }
      if (ambientRafRef.current) {
        window.cancelAnimationFrame(ambientRafRef.current);
      }
      if (audioCtxRef.current) {
        void audioCtxRef.current.close();
      }
      sourceRef.current = null;
      gainNodeRef.current = null;
      analyserRef.current = null;
      dataArrayRef.current = null;
    };
  }, [revokePlaylistUrls]);

  const progressPercent = useMemo(() => {
    if (!duration) {
      return 0;
    }
    return clamp((currentTime / duration) * 100, 0, 100);
  }, [currentTime, duration]);

  const pipDisabled = isAudioFile || !pipSupported;
  const hiddenUiClass = forceHide || hideUi ? "hide-ui" : "";
  const sliderVolume = muted ? 0 : volume;
  const hasQueue = playlist.length > 1;
  const canGoNext =
    playlist.length > 1 &&
    (repeatAll || shuffleMode || currentTrackIndex < playlist.length - 1);
  const canGoPrevious =
    hasMedia &&
    (currentTime > 3 ||
      (playlist.length > 1 &&
        (repeatAll || shuffleMode || currentTrackIndex > 0)));
  const upcomingTracks =
    currentTrackIndex >= 0
      ? playlist.slice(currentTrackIndex + 1, currentTrackIndex + 5)
      : [];
  const visualizerEnabled =
    isAudioFile &&
    hasMedia &&
    Boolean(playlist[currentTrackIndex]?.revokeOnDispose);

  const playerRootClassName = [
    "player-root",
    hiddenUiClass,
    touchUi ? "touch-ui" : "",
    forceHide ? "force-hide" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const isSwipeTargetBlocked = (target: EventTarget | null): boolean => {
    if (!(target instanceof Element)) {
      return true;
    }
    return Boolean(
      target.closest("#controls") ||
        target.closest("#queue-panel") ||
        target.closest("#context-menu") ||
        target.closest("#drop-zone"),
    );
  };

  const onPlayerMouseLeave = () => {
    if (touchUi || forceHide || !hasMedia) {
      return;
    }
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    setHideUi(true);
  };

  const onPlayerMouseEnter = () => {
    if (touchUi || forceHide) {
      return;
    }
    resetIdleTimer();
  };

  const onPlayerTouchStart = (event: ReactTouchEvent<HTMLDivElement>) => {
    if (!touchUi || !hasMedia) {
      swipeStartRef.current = null;
      return;
    }
    if (event.touches.length !== 1) {
      swipeStartRef.current = null;
      return;
    }
    if (isSwipeTargetBlocked(event.target)) {
      swipeStartRef.current = null;
      return;
    }
    const touch = event.touches[0];
    swipeStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const onPlayerTouchEnd = (event: ReactTouchEvent<HTMLDivElement>) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    if (!start || !touchUi || !hasMedia || event.changedTouches.length === 0) {
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (absY < SWIPE_MIN_DISTANCE || absY < absX + SWIPE_VERTICAL_BIAS) {
      return;
    }

    if (deltaY > 0) {
      if (!forceHide) {
        setForceHide(true);
      }
      setHideUi(true);
      return;
    }

    if (forceHide || hideUi) {
      setForceHide(false);
      setHideUi(false);
    }
  };

  return (
    <div
      id="player-root"
      ref={playerRootRef}
      className={playerRootClassName}
      onMouseLeave={onPlayerMouseLeave}
      onMouseEnter={onPlayerMouseEnter}
      onTouchStart={onPlayerTouchStart}
      onTouchEnd={onPlayerTouchEnd}
      onTouchCancel={() => {
        swipeStartRef.current = null;
      }}
    >
      <div id="ambient-glow" ref={ambientGlowRef} />

      {showNerdStats ? (
        <div id="nerd-stats">
          <div className="stats-title">STATS FOR NERDS</div>
          <div className="stats-grid">
            <span>Mime Type:</span>
            <span className="stats-value">{isAudioFile ? "Audio" : "Video"}/Blob</span>
            <span>Resolution:</span>
            <span className="stats-value">{resolution}</span>
            <span>Volume:</span>
            <span className="stats-value">{Math.round(volume * 100)}%</span>
            <span>Boost:</span>
            <span className="stats-value">{currentBoost}x</span>
            <span>Speed:</span>
            <span className="stats-value">{playbackRate}x</span>
            <span>Buffered:</span>
            <span className="stats-value">{bufferedAhead.toFixed(2)}s</span>
            <span>Current:</span>
            <span className="stats-value">{currentTime.toFixed(2)}s</span>
          </div>
        </div>
      ) : null}

      {hasMedia && hasQueue ? (
        <div id="queue-panel" className="queue-ready">
          <div className="queue-title">Queue</div>
          <div className="queue-now">Now: {playlist[currentTrackIndex]?.displayName ?? audioTitle}</div>
          <div className="queue-list">
            {upcomingTracks.length > 0 ? (
              upcomingTracks.map((item, index) => {
                const absoluteIndex = currentTrackIndex + index + 1;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className="queue-item"
                    onClick={() => {
                      playTrackAtIndex(absoluteIndex, true);
                    }}
                  >
                    <span className="queue-item-index">{absoluteIndex + 1}.</span>
                    <span className="queue-item-name">{item.displayName}</span>
                  </button>
                );
              })
            ) : (
              <div className="queue-empty">No more tracks queued</div>
            )}
          </div>
          <div className="queue-flags">
            <span>{shuffleMode ? "Shuffle on" : "Shuffle off"}</span>
            <span>{repeatAll ? "Repeat all" : "No repeat-all"}</span>
          </div>
        </div>
      ) : null}

      {contextMenu.visible ? (
        <div
          id="context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="menu-item" onClick={() => runContextAction(cycleBoost)}>
            <span>Boost Vol</span>
            <span className="menu-item-indicator">{currentBoost * 100}%</span>
          </div>
          <div className="menu-item" onClick={() => runContextAction(takeSnapshot)}>
            <span>Take Snapshot</span>
          </div>
          <div className="menu-item" onClick={() => runContextAction(() => setShowNerdStats((prev) => !prev))}>
            <span>Stats for Nerds</span>
          </div>
          <hr className="menu-divider" />
          <div className="menu-item" onClick={() => window.location.reload()}>
            <span>Reset Player</span>
          </div>
        </div>
      ) : null}

      <div id="player-container">
        <div
          id="media-layer"
          onClick={togglePlay}
          onMouseEnter={() => {
            if (!touchUi && hasMedia && !forceHide && !hoveringControls) {
              setHideUi(true);
            }
          }}
          onMouseMove={() => {
            if (!touchUi && !forceHide) {
              resetIdleTimer();
            }
          }}
        >
          {isAudioFile && hasMedia ? (
            <div id="album-art-container">
              {albumArtUrl ? (
                <img id="album-art" src={albumArtUrl} alt="Album Art" />
              ) : (
                <div id="no-art-placeholder">
                  <svg width="96" height="96" viewBox="0 0 24 24" fill="currentColor" style={{ color: "var(--accent)", opacity: 0.5 }}>
                    <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
                  </svg>
                </div>
              )}
            </div>
          ) : null}

          {isAudioFile && hasMedia ? <h1 id="audio-title">{audioTitle}</h1> : null}

          <video
            id="main-video"
            ref={videoRef}
            className={!isAudioFile && hasMedia ? "" : "hidden-media"}
            crossOrigin="anonymous"
            controlsList="nodownload"
          />
        </div>

        <canvas
          id="visualizer-canvas"
          ref={visualizerRef}
          className={visualizerEnabled ? "" : "hidden-media"}
        />
      </div>

      <div id="animation-layer">
        {popups.map((popup) => {
          if (popup.kind === "text") {
            return (
              <div key={popup.id} className="skip-indicator">
                <span className="popup-text">{popup.text}</span>
              </div>
            );
          }

          const forward = popup.direction === "forward";
          return (
            <div key={popup.id} className="skip-indicator">
              {forward ? (
                <svg className="skip-icon" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z" />
                </svg>
              ) : (
                <svg className="skip-icon" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z" />
                </svg>
              )}
              <span className="popup-text">{forward ? "+5s" : "-5s"}</span>
            </div>
          );
        })}
      </div>

      {!hasMedia ? (
        <div
          id="drop-zone"
          onClick={openFilePicker}
          onKeyDown={onDropZoneKeyDown}
          role="button"
          tabIndex={0}
          aria-label="Upload media files"
        >
          <UploadFluidBackground
            className="upload-fluid-layer"
            color1="#030916"
            color2="#0b2f75"
            color3="#1a73d9"
            color4="#6fe6ff"
          />

          <div className="upload-panel">
            <div className="upload-callout">
              <div className="upload-circle">
                <svg width="64" height="64" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z" />
                </svg>
              </div>
              <p className="upload-title">Upload Media</p>
              <p className="upload-subtitle">Select one or more files</p>
            </div>

            <div className="upload-actions" onClick={(event) => event.stopPropagation()}>
              <button type="button" className="upload-action-primary" onClick={openFilePicker}>
                Choose Files
              </button>
            </div>

            <div className="upload-footnote">
              Supports: mp4, mkv, mov, webm, mp3, wav, flac, m4a, ogg
            </div>
          </div>
        </div>
      ) : null}

      <input
        type="file"
        ref={fileInputRef}
        className="hidden-input"
        accept="video/*,audio/*"
        multiple
        onChange={onFileInputChange}
      />

      <div
        id="controls"
        className={`controls-overlay ${hasMedia ? "controls-ready" : ""}`}
        onMouseEnter={onControlsMouseEnter}
        onMouseLeave={onControlsMouseLeave}
      >
        <div
          className="seek-container"
          ref={seekAreaRef}
          onMouseMove={onSeekMouseMove}
          onMouseLeave={() => setSeekTooltip((prev) => ({ ...prev, visible: false }))}
        >
          {seekTooltip.visible ? (
            <div id="time-tooltip" style={{ left: `${seekTooltip.left}px` }}>
              {seekTooltip.text}
            </div>
          ) : null}

          <div className="seek-bg">
            <div id="progress-filled" style={{ width: `${progressPercent}%` }} />
          </div>

          <input
            type="range"
            id="seek-input"
            value={progressPercent}
            min={0}
            max={100}
            step={0.01}
            onChange={onSeekInput}
          />
        </div>

        <div className="controls-row">
          <div className="controls-left">
            <button
              type="button"
              aria-label="Previous track"
              className={`icon-button ${canGoPrevious ? "" : "disabled"}`}
              disabled={!canGoPrevious}
              onClick={(event) => {
                onControlClick(event);
                playPreviousTrack();
              }}
            >
              <svg width="32" height="32" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 6h2v12H6zm3.5 6L20 18V6z" />
              </svg>
            </button>

            <button
              type="button"
              aria-label={isPlaying ? "Pause" : "Play"}
              className="icon-button play-button"
              onClick={(event) => {
                onControlClick(event);
                togglePlay();
              }}
            >
              {!isPlaying ? (
                <svg width="40" height="40" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              ) : (
                <svg width="40" height="40" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                </svg>
              )}
            </button>

            <button
              type="button"
              aria-label="Next track"
              className={`icon-button ${canGoNext ? "" : "disabled"}`}
              disabled={!canGoNext}
              onClick={(event) => {
                onControlClick(event);
                playNextTrack("manual");
              }}
            >
              <svg width="32" height="32" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 6h2v12h-2zM4 6l10.5 6L4 18z" />
              </svg>
            </button>

            <div className="volume-block">
              <button
                type="button"
                aria-label={muted ? "Unmute" : "Mute"}
                className="icon-button"
                onClick={(event) => {
                  onControlClick(event);
                  toggleMute();
                }}
              >
                <svg width="32" height="32" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 9v6h4l5 5V4L7 9H3z" />
                </svg>
              </button>

              <input
                id="volume-slider"
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={sliderVolume}
                onClick={(event) => event.stopPropagation()}
                onChange={onVolumeInput}
              />

              <span id="boost-tag">{currentBoost}x</span>
            </div>

            <div className="time-display">
              <span>{formatTime(currentTime)}</span>
              <span className="time-divider">/</span>
              <span className="time-muted">{formatTime(duration)}</span>
            </div>

            {hasQueue ? (
              <span className="queue-count-pill">
                Track {currentTrackIndex + 1} of {playlist.length}
              </span>
            ) : null}
          </div>

          <div className="controls-right">
            <button
              type="button"
              aria-label="Toggle loop one"
              className={`icon-button ${isLoop ? "active" : ""}`}
              onClick={(event) => {
                onControlClick(event);
                toggleLoop();
              }}
            >
              <svg width="32" height="32" fill="currentColor" viewBox="0 0 24 24">
                <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z" />
              </svg>
            </button>

            <button
              type="button"
              aria-label="Toggle repeat all"
              className={`icon-button ${repeatAll ? "active" : ""}`}
              onClick={(event) => {
                onControlClick(event);
                toggleRepeatAll();
              }}
            >
              <svg width="30" height="30" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 7h9l-1.9-1.9L14.5 3.7 19 8.2l-4.5 4.5-1.4-1.4L15 9H6c-1.1 0-2 .9-2 2v1H2v-1c0-2.2 1.8-4 4-4z" />
                <path d="M18 17H9l1.9 1.9-1.4 1.4L5 15.8l4.5-4.5 1.4 1.4L9 15h9c1.1 0 2-.9 2-2v-1h2v1c0 2.2-1.8 4-4 4z" />
              </svg>
            </button>

            <button
              type="button"
              aria-label="Toggle shuffle"
              className={`icon-button ${shuffleMode ? "active" : ""}`}
              onClick={(event) => {
                onControlClick(event);
                toggleShuffleMode();
              }}
            >
              <svg width="30" height="30" fill="currentColor" viewBox="0 0 24 24">
                <path d="M4 7h4l3 4 3-4h6v2h-5l-3 4 3 4h5v2h-6l-3-4-3 4H4v-2h4l3-4-3-4H4V7z" />
              </svg>
            </button>

            <button
              type="button"
              aria-label="Change playback speed"
              className="speed-button"
              onClick={(event) => {
                onControlClick(event);
                cycleSpeed();
              }}
            >
              Speed: {playbackRate}x
            </button>

            <button
              type="button"
              aria-label="Toggle picture in picture"
              className={`icon-button ${pipDisabled ? "disabled" : ""}`}
              disabled={pipDisabled}
              onClick={(event) => {
                onControlClick(event);
                void togglePIP();
              }}
            >
              <svg width="32" height="32" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 11h-8v6h8v-6zm4 8V4.98C23 3.88 22.1 3 21 3H3c-1.1 0-2 .88-2 1.98V19c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2zm-2 .02H3V4.97h18v14.05z" />
              </svg>
            </button>

            <button
              type="button"
              aria-label="Toggle fullscreen"
              className="icon-button"
              onClick={(event) => {
                onControlClick(event);
                toggleFullscreen();
              }}
            >
              <svg width="32" height="32" fill="currentColor" viewBox="0 0 24 24">
                <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
